import React, { useState } from 'react';
import PanelHeader from '../PanelHeader';
import { useEditor } from '../EditorContext';
import {
  BUTTON_SHAPES,
  BUTTON_ACTIONS,
  ROADSIDE_CHARGE_ACTIONS,
  MAX_BUTTONS,
  FONT_LIST,
  isRoadsideChargeAction,
  getRoadsideDefaultAmount,
} from '../constants';
import { addButton, setObjectFill } from '../../canvasEditor';
import { countButtons, resolveButtonPair, iconUrl } from '../fabricHelpers';
import { loadStickerFromUrl } from '../../canvasEditor';
import TemplateAutocomplete from '../TemplateAutocomplete';

const TEMPLATE_REPLY_ACTIONS = new Set(['sendtemplateonreply', 'sendtemplateon911']);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isTemplateId(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

function parseAction(actionStr) {
  const parts = (actionStr || 'link:').split(/:(.+)?/);
  return { type: parts[0] || 'link', value: parts[1] || '' };
}

function isTransparentFill(fill) {
  if (fill == null || fill === '') return false;
  const value = String(fill).trim().toLowerCase();
  if (value === 'transparent') return true;
  const rgbaMatch = value.match(/^rgba?\(([^)]+)\)$/);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((part) => part.trim());
    if (parts.length === 4 && Number(parts[3]) === 0) return true;
  }
  return false;
}

function resolveButtonFillColor(shape) {
  if (!shape) return '#008000';
  if (isTransparentFill(shape.fill)) {
    return shape.buttonSavedFill || '#008000';
  }
  return typeof shape.fill === 'string' && shape.fill.startsWith('#') ? shape.fill : '#008000';
}

export default function ButtonsPanel() {
  const { fabricRef, selection, refreshLayers, currentTemplateId } = useEditor();
  const [iconPage, setIconPage] = useState(0);

  const { shape: buttonShape, text: buttonText } = resolveButtonPair(selection);

  const commit = () => {
    fabricRef.current?.renderAll();
    refreshLayers?.();
  };

  const addBtn = (shape) => {
    const fc = fabricRef.current;
    if (!fc) return;
    if (countButtons(fc) >= MAX_BUTTONS) {
      window.alert(`Maximum ${MAX_BUTTONS} buttons allowed`);
      return;
    }
    addButton(fc, 'Button', { shape });
    refreshLayers?.();
  };

  const setAction = (type, value) => {
    if (!buttonShape) return;
    buttonShape.set('action', `${type}:${value}`);
    commit();
  };

  const patchShape = (patch) => {
    if (!buttonShape) return;
    buttonShape.set(patch);
    if (buttonShape.textObj) {
      const t = buttonShape.textObj;
      if (buttonShape.type === 'circle') {
        const r = (buttonShape.radius || 60) * (buttonShape.scaleX || 1) * 2;
        t.set({ left: buttonShape.left + r / 2, top: buttonShape.top + r / 2 });
      } else {
        t.set({
          left: buttonShape.left + (buttonShape.width * (buttonShape.scaleX || 1)) / 2,
          top: buttonShape.top + (buttonShape.height * (buttonShape.scaleY || 1)) / 2,
        });
      }
    }
    commit();
  };

  const patchText = (patch) => {
    const text = buttonText || buttonShape?.textObj;
    if (!text) return;
    text.set(patch);
    commit();
  };

  const applyIcon = async (index) => {
    if (!buttonShape) return;
    const fc = fabricRef.current;
    const text = buttonShape.textObj;
    if (!text) return;

    if (index === 0) {
      if (text.textObj) {
        fc.remove(text.textObj);
        text.textObj = null;
      }
      fc.renderAll();
      return;
    }

    const url = iconUrl(index);
    try {
      const icon = await loadStickerFromUrl(url, true);
      const idx = (buttonShape.niceName || '').match(/\d+/)?.[0] || '1';
      icon.set({
        name: 'icon',
        niceName: `button icon ${idx}`,
        scaleX: 0.15,
        scaleY: 0.15,
        url,
        uploadUrl: url,
      });
      if (text.textObj) fc.remove(text.textObj);
      text.textObj = icon;
      icon.textObj = text;
      icon.left = buttonShape.left + 10;
      icon.top = buttonShape.top + 10;
      fc.add(icon);
      fc.renderAll();
      refreshLayers?.();
    } catch (err) {
      console.warn('Icon load failed', url, err);
    }
  };

  const { type: actionType, value: actionValue } = parseAction(buttonShape?.action);
  const showTemplatePicker = TEMPLATE_REPLY_ACTIONS.has(actionType);
  const showRoadsideAmount = isRoadsideChargeAction(actionType);
  const roadsideActionMeta = ROADSIDE_CHARGE_ACTIONS.find((a) => a.id === actionType);
  const buttonFillTransparent = buttonShape ? isTransparentFill(buttonShape.fill) : false;
  const buttonFillColor = resolveButtonFillColor(buttonShape);

  const handleActionTypeChange = (newType) => {
    let newValue = actionValue;
    if (isRoadsideChargeAction(newType)) {
      newValue = String(getRoadsideDefaultAmount(newType));
    } else if (newType === 'link') {
      newValue = isRoadsideChargeAction(actionType) ? 'https://example.com' : actionValue;
    } else if (newType === 'phone') {
      newValue = isRoadsideChargeAction(actionType) ? '' : actionValue;
    } else {
      newValue = '';
    }
    setAction(newType, newValue);
  };

  const setButtonTransparent = (transparent) => {
    if (!buttonShape) return;
    if (transparent) {
      if (!isTransparentFill(buttonShape.fill)) {
        buttonShape.set('buttonSavedFill', resolveButtonFillColor(buttonShape));
        buttonShape.set('fill', 'transparent');
      }
    } else {
      buttonShape.set('fill', buttonShape.buttonSavedFill || '#008000');
    }
    commit();
  };

  const setButtonFillColor = (color) => {
    if (!buttonShape) return;
    buttonShape.set('buttonSavedFill', color);
    setObjectFill(buttonShape, color);
    commit();
  };

  const iconsStart = iconPage * 32 + 1;

  return (
    <div className="pixie-panel">
      <PanelHeader title="Buttons" icon="fa-square" />
      <div className="pixie-shape-grid">
        {BUTTON_SHAPES.map((s) => (
          <button key={s.id} type="button" className="pixie-shape-tile" onClick={() => addBtn(s.id)}>
            <i className={`fa-solid fa-${s.id === 'circle' ? 'circle' : 'square'}`} />
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      {buttonShape && (
        <div className="pixie-props" onMouseDown={(e) => e.stopPropagation()}>
          <PanelHeader title="Button Properties" icon="fa-sliders" />
          <label className="pixie-field">
            Action
            <select
              value={actionType}
              onChange={(e) => handleActionTypeChange(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <optgroup label="Standard">
                {BUTTON_ACTIONS.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </optgroup>
              <optgroup label="Roadside Charge">
                {ROADSIDE_CHARGE_ACTIONS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} (${a.defaultAmount})
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          {showRoadsideAmount && (
            <label className="pixie-field">
              {roadsideActionMeta ? `${roadsideActionMeta.label} amount ($)` : 'Charge amount ($)'}
              <input
                type="number"
                min={0}
                step={0.01}
                value={actionValue}
                placeholder={String(getRoadsideDefaultAmount(actionType))}
                onChange={(e) => setAction(actionType, e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </label>
          )}
          {(actionType === 'link' || actionType === 'phone') && (
            <label className="pixie-field">
              {actionType === 'phone' ? 'Phone' : 'URL'}
              <input
                type="text"
                value={actionValue}
                onChange={(e) => setAction(actionType, e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </label>
          )}
          {showTemplatePicker && (
            <label className="pixie-field">
              {actionType === 'sendtemplateon911' ? '911 Template' : 'Reply Template'}
              <TemplateAutocomplete
                value={isTemplateId(actionValue) ? actionValue : ''}
                onChange={(templateId) => setAction(actionType, templateId)}
                excludeId={currentTemplateId}
                type="alert"
                placeholder="Type to search templates…"
                emptyLabel="No templates found"
              />
            </label>
          )}
          <label className="pixie-field pixie-check">
            <input
              type="checkbox"
              checked={buttonFillTransparent}
              onChange={(e) => setButtonTransparent(e.target.checked)}
              onMouseDown={(e) => e.stopPropagation()}
            />
            Transparent background
          </label>
          <label className="pixie-field">
            Fill
            <input
              type="color"
              value={buttonFillColor}
              disabled={buttonFillTransparent}
              onChange={(e) => setButtonFillColor(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </label>
          {buttonShape.type === 'rect' && (
            <label className="pixie-field">
              Corner radius
              <input
                type="range"
                min={0}
                max={50}
                value={buttonShape.rx || 0}
                onChange={(e) => patchShape({ rx: Number(e.target.value), ry: Number(e.target.value) })}
              />
            </label>
          )}

          <PanelHeader title="Text" icon="fa-font" />
          <label className="pixie-field">
            Label
            <input
              type="text"
              value={(buttonText || buttonShape.textObj)?.text || ''}
              onChange={(e) => patchText({ text: e.target.value })}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </label>
          <label className="pixie-field">
            Font
            <select
              value={(buttonText || buttonShape.textObj)?.fontFamily || 'Open Sans'}
              onChange={(e) => patchText({ fontFamily: e.target.value })}
            >
              {FONT_LIST.map((font) => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </label>
          <label className="pixie-field">
            Font size
            <input
              type="range"
              min={10}
              max={48}
              value={(buttonText || buttonShape.textObj)?.fontSize || 24}
              onChange={(e) => patchText({ fontSize: Number(e.target.value) })}
            />
          </label>
          <label className="pixie-field">
            Text color
            <input
              type="color"
              onChange={(e) => patchText({ fill: e.target.value })}
            />
          </label>

          <PanelHeader title="Icon" icon="fa-icons" />
          <div className="pixie-icon-grid">
            <button type="button" className="pixie-icon-tile" onClick={() => applyIcon(0)}>
              NONE
            </button>
            {Array.from({ length: 32 }, (_, i) => iconsStart + i).map((n) => (
              n <= 256 && (
                <button key={n} type="button" className="pixie-icon-tile" onClick={() => applyIcon(n)}>
                  <img src={iconUrl(n)} alt="" loading="lazy" />
                </button>
              )
            ))}
          </div>
          <div className="pixie-icon-pager">
            <button type="button" disabled={iconPage === 0} onClick={() => setIconPage((p) => p - 1)}>Prev</button>
            <button type="button" disabled={iconsStart + 32 > 256} onClick={() => setIconPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
